'use client';

import { StaticImageData } from 'next/image';
import React, { useMemo, useState } from 'react';
import { IoCheckmarkDone } from 'react-icons/io5';
import { TbCheck, TbInfoCircle } from 'react-icons/tb';

import {
    ActionIcon,
    Alert,
    Box,
    Button,
    Card,
    Checkbox,
    Divider,
    Group,
    NumberInput,
    Paper,
    Select,
    SimpleGrid,
    Space,
    Stack,
    Stepper,
    Text,
    Textarea,
    TextInput,
    Title,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import { useForm } from '@mantine/form';

import { CREDIT_COST } from 'models';
import { IntlHelpers } from 'helpers/IntlHelpers';

import pplControllerImage from '../../../../app/(app)/controller/ppl_controller_front.png';
import { COUNTRIES } from '../../../../data/countries';
import { CONTROLLER_INCLUDED_HARDWARE } from '../../data/controller-hardware';
import { ImageBlock } from '../blocks/ImageBlock';

import cx from './PriceConfigurator.module.css';

type HardwareType = 'starter' | 'regular' | 'none' | null;
type HardwareTypeKey = Exclude<HardwareType, null>;

type LicenseType = 'buy' | null;
type LicenseTypeKey = Exclude<LicenseType, null>;

const PRICES = {
    starterKit: 2499,
    regularController: 3499,
    licensePerKW: {
        buyOneTime: 10, // $/kW
    },
    credits: {
        minimum: 25000,
        costPerCredit: CREDIT_COST, // $/credit
    },
};

const hardwareDetails: Partial<
    Record<
        HardwareTypeKey,
        {
            bullets: string[];
            includedHardware?: string[];
            note?: string;
            images?: { image: StaticImageData; caption?: string }[];
        }
    >
> = {
    none: {
        bullets: [
            'Compatible with Phoenix Contact AXC F 2152 hardware. Install the app from the PLCnext Store and activate your software license.',
        ],
    },
    starter: {
        bullets: ['90-day full-feature trial with no capacity limits.'],
        includedHardware: CONTROLLER_INCLUDED_HARDWARE,
        note: 'Limited one per customer. Ideal for evaluation purposes.',
        images: [{ image: pplControllerImage }],
    },
    regular: {
        bullets: ['Built for permanent field deployments.'],
        includedHardware: CONTROLLER_INCLUDED_HARDWARE,
        images: [{ image: pplControllerImage }],
    },
};

const licenseDetails: Partial<Record<LicenseTypeKey, { bullets: string[]; note?: string }>> = {
    buy: {
        bullets: [
            'Priced at $10 per kW of managed capacity, a one-time purchase for lifetime, local control with no subscription.',
            'Includes all software features, live & historical dashboards, real-time diagnostics, and continuous updates.',
        ],
    },
};

type SelectCardProps = {
    title: string;
    description?: string;
    descriptionSize?: string;
    price?: string;
    selected?: boolean;
    items?: string[];
    onClick?: () => void;
};

const SelectCard: React.FC<SelectCardProps> = ({
    title,
    description,
    descriptionSize = 'lg',
    price,
    selected,
    items,
    onClick,
}) => {
    return (
        <Card
            withBorder
            radius="md"
            p="md"
            onClick={onClick}
            style={{
                cursor: onClick ? 'pointer' : 'not-allowed',
                borderColor: selected ? '#000' : undefined,
                boxShadow: selected ? '0 0 0 2px #000' : undefined,
                opacity: onClick ? 1 : 0.8,
            }}
        >
            <Stack gap={4} style={{ minHeight: 96, justifyContent: 'center' }}>
                <Text fw={800} fz="lg">
                    {title}
                </Text>
                {description && (
                    <Text size={descriptionSize} c={descriptionSize === 'lg' ? undefined : 'dimmed'}>
                        {description}
                    </Text>
                )}
                {price && (
                    <Text size="lg" fw={600}>
                        {price}
                    </Text>
                )}
                {items && (
                    <>
                        <Space h={4} />
                        <Stack gap={0}>
                            {items.map((b, i) => (
                                <Text size="md" c="dimmed" key={i}>
                                    {b}
                                </Text>
                            ))}
                        </Stack>
                    </>
                )}
            </Stack>
        </Card>
    );
};

interface PriceConfiguratorProps {
    initialHardware?: HardwareType;
}

export const PriceConfigurator: React.FC<PriceConfiguratorProps> = ({ initialHardware = null }) => {
    const [active, setActive] = useState(0);
    const [, setModalOpened] = useState(false);
    const [, setSelectedImages] = useState<Array<{ image: StaticImageData; caption?: string }>>([]);
    const [, setSelectedImageIndex] = useState(0);

    const handleImageClick = (images: Array<{ image: StaticImageData; caption?: string }>, index: number) => {
        setSelectedImages(images);
        setSelectedImageIndex(index);
        setModalOpened(true);
    };

    const [hardware, setHardware] = useState<HardwareType>(initialHardware);
    const [licenseType, setLicenseType] = useState<LicenseType>(null);
    const [capacityMW, setCapacityMW] = useState<number | ''>(1);
    const [includeCredits, setIncludeCredits] = useState(true);
    const [creditsAmount, setCreditsAmount] = useState<number | ''>(PRICES.credits.minimum);
    const [showQuoteForm, setShowQuoteForm] = useState(false);
    const [quoteSubmitted, setQuoteSubmitted] = useState(false);

    const quoteForm = useForm({
        initialValues: {
            firstName: '',
            lastName: '',
            email: '',
            company: '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            zip: '',
            country: '',
            message: '',
        },
        validate: {
            firstName: (v) => (v.trim().length === 0 ? 'First name is required' : null),
            lastName: (v) => (v.trim().length === 0 ? 'Last name is required' : null),
            email: (v) => (/^\S+@\S+$/.test(v) ? null : 'Please enter a valid email'),
            company: (v) => (v.trim().length === 0 ? 'Company name is required' : null),
            addressLine1: (v) => (v.trim().length === 0 ? 'Address line 1 is required' : null),
            addressLine2: () => null,
            city: (v) => (v.trim().length === 0 ? 'City is required' : null),
            zip: (v) => (v.trim().length === 0 ? 'ZIP code is required' : null),
            country: (v) => (v ? null : 'Country is required'),
            message: () => null,
        },
    });

    const handleSubmitQuote = async (values: typeof quoteForm.values) => {
        const content = {
            ...values,
            configuration: {
                hardware,
                licenseType,
                capacityMW: typeof capacityMW === 'number' ? capacityMW : 0,
                credits: includeCredits ? creditsNumeric : 0,
                totals,
            },
        };
        await fetch('/api/formSubmissions', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                type: 'ppl-price-configurator',
                name: `Price Configurator Request: ${values.firstName} ${values.lastName} (${values.company})`,
                content,
            }),
        });

        quoteForm.reset();
        setShowQuoteForm(false);
        setQuoteSubmitted(true);
    };

    // Derived controls for flow
    // Capacity is entered in MW; the license is priced per kW of managed capacity.
    const capacityNumeric = useMemo(() => (typeof capacityMW === 'number' ? capacityMW : 0), [capacityMW]);
    const capacityKW = useMemo(() => capacityNumeric * 1000, [capacityNumeric]);

    const licenseFee = useMemo(() => {
        if (!capacityKW || licenseType !== 'buy') return 0;
        return capacityKW * PRICES.licensePerKW.buyOneTime;
    }, [capacityKW, licenseType]);

    const hardwareFee = useMemo(() => {
        if (hardware === 'starter') return PRICES.starterKit;
        if (hardware === 'regular') return PRICES.regularController;
        // 'none' or null yields $0 hardware
        return 0;
    }, [hardware]);

    // Estimate for displaying on the license card regardless of chosen license type
    const estimateBuy = useMemo(() => capacityKW * PRICES.licensePerKW.buyOneTime, [capacityKW]);

    const creditsNumeric = useMemo(() => (typeof creditsAmount === 'number' ? creditsAmount : 0), [creditsAmount]);
    const creditsBelowMinimum = includeCredits && creditsNumeric < PRICES.credits.minimum;
    const creditsCost = useMemo(
        () => (includeCredits ? creditsNumeric * PRICES.credits.costPerCredit : 0),
        [includeCredits, creditsNumeric],
    );

    const totals = useMemo(() => {
        const oneTime =
            (hardware === 'starter' ? PRICES.starterKit : hardware === 'regular' ? PRICES.regularController : 0) +
            (licenseType === 'buy' ? licenseFee : 0) +
            creditsCost;

        return { oneTime };
    }, [hardware, licenseFee, licenseType, creditsCost]);

    // Determine visible steps dynamically based on selections
    const stepKeys = useMemo(() => {
        const keys: Array<'hardware' | 'license' | 'support' | 'summary'> = ['hardware'];
        if (hardware === 'starter' || hardware === null) {
            keys.push('support', 'summary');
            return keys;
        }
        // hardware is 'regular' or 'none' => both go through license flow
        keys.push('license');
        keys.push('support', 'summary');
        return keys;
    }, [hardware]);

    const goNext = () => setActive((cur) => Math.min(cur + 1, stepKeys.length - 1));
    const goPrev = () => setActive((cur) => Math.max(cur - 1, 0));

    // Flow control based on selections
    const handleSelectHardware = (value: HardwareType) => {
        setHardware(value);
        setLicenseType(value === 'regular' || value === 'none' ? 'buy' : null);
    };

    const handleLicenseChange = (value: LicenseType) => {
        setLicenseType(value);
    };

    const Summary = () => (
        <>
            <Stack gap="md">
                <Title order={4} fz="lg" fw={800}>
                    Summary
                </Title>
                <Divider />
                <Text size="lg" fw={600}>
                    Hardware
                </Text>
                <Box pl="md">
                    <Group justify="space-between">
                        <Text size="lg">
                            {hardware === 'starter'
                                ? 'Starter Kit'
                                : hardware === 'regular'
                                  ? 'Deployment Kit'
                                  : 'No Hardware Selected'}
                        </Text>
                        <Text size="lg">{IntlHelpers.formatPrice(hardwareFee, 0)}</Text>
                    </Group>
                    {(hardware === 'starter' || hardware === 'regular') && (
                        <Text size="md" mt={4} c="dimmed">
                            Includes {CONTROLLER_INCLUDED_HARDWARE.length} hardware components
                            {hardware === 'starter' ? ' and a 90-day full-feature trial' : ''}.
                        </Text>
                    )}
                </Box>

                <Text size="lg" fw={600}>
                    License
                </Text>
                <Box pl="md">
                    <Group justify="space-between">
                        <Text size="lg">Software License: {licenseType ? 'Buy' : 'N/A'}</Text>
                        <Text size="lg">{IntlHelpers.formatPrice(licenseFee, 0)}</Text>
                    </Group>
                    <Text size="md" mt={4} c="dimmed">
                        {licenseType
                            ? `Managed Capacity: ${IntlHelpers.formatNumber(capacityKW)} kW @ $${PRICES.licensePerKW.buyOneTime}/kW`
                            : hardware === 'starter'
                              ? '90-Day Trial License'
                              : ''}
                    </Text>
                </Box>

                <Text size="lg" fw={600}>
                    Support
                </Text>
                <Box pl="md">
                    <Group justify="space-between">
                        <Text size="lg">Engineering Support: {includeCredits ? 'DCIDE Credits' : 'Pay-as-you-go'}</Text>
                        <Text size="lg">{IntlHelpers.formatPrice(creditsCost, 0)}</Text>
                    </Group>
                    <Text size="md" mt={4} c="dimmed">
                        {includeCredits
                            ? `${IntlHelpers.formatNumber(creditsNumeric)} credits.`
                            : 'Add credits anytime for paid engineering work.'}
                    </Text>
                </Box>

                <Divider />
                <Group justify="space-between">
                    <Text size="lg" fw={600}>
                        Total
                    </Text>
                    <Text size="lg" fw={700}>
                        {IntlHelpers.formatPrice(totals.oneTime, 0)}
                    </Text>
                </Group>
                <Text size="md" c="dimmed">
                    Engineering support via DCIDE credits - incoming questions always free.
                </Text>
            </Stack>
        </>
    );

    return (
        <Box
            className="animation-border-spin"
            style={{
                '--background-color': 'var(--mantine-color-green-1)',
                '--animation-color': 'var(--mantine-color-cyan-1)',
                '--border-width': '6px',
                borderRadius: 20,
            }}
        >
            <Card p="xl" radius="lg" shadow="xl">
                <Card.Section m={-32} mb="xl" p="xl" c="white" bg="black">
                    <Title order={3} fz="xl" fw={800}>
                        Price Configurator
                    </Title>
                </Card.Section>

                <Stack gap="xl">
                    <Stepper
                        active={active}
                        onStepClick={setActive}
                        allowNextStepsSelect={false}
                        size="lg"
                        completedIcon={<IoCheckmarkDone size={24} />}
                        color="black"
                        styles={{
                            stepIcon: {
                                borderWidth: 3,
                                color: 'black',
                            },
                            content: {
                                paddingTop: 'var(--mantine-spacing-xl)',
                            },
                        }}
                    >
                        <Stepper.Step label="Hardware" description="">
                            <Stack gap="xl">
                                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                                    <SelectCard
                                        title="Starter Kit"
                                        description="Everything you need to get started"
                                        price={IntlHelpers.formatPrice(PRICES.starterKit, 0)}
                                        selected={hardware === 'starter'}
                                        onClick={() => handleSelectHardware('starter')}
                                    />
                                    <SelectCard
                                        title="Deployment Kit"
                                        description="Hardware for permanent deployments"
                                        price={IntlHelpers.formatPrice(PRICES.regularController, 0)}
                                        selected={hardware === 'regular'}
                                        onClick={() => handleSelectHardware('regular')}
                                    />
                                    <SelectCard
                                        title="Use My Own Controller"
                                        description="Deploy your own hardware"
                                        price={`$0`}
                                        selected={hardware === 'none'}
                                        onClick={() => handleSelectHardware('none')}
                                    />
                                </SimpleGrid>
                                {hardware && hardwareDetails[hardware] && (
                                    <Stack gap={4}>
                                        {(hardwareDetails[hardware]?.bullets ?? []).map((b, i) => (
                                            <Text key={i} fz="lg" c="dimmed">
                                                {b}
                                            </Text>
                                        ))}
                                        {hardwareDetails[hardware]?.note && (
                                            <Text fs="italic" fz="lg" c="dimmed">
                                                {hardwareDetails[hardware]?.note}
                                            </Text>
                                        )}
                                    </Stack>
                                )}
                                {hardware && hardwareDetails[hardware]?.includedHardware && (
                                    <Stack gap={8}>
                                        <Text fz="lg" fw={600}>
                                            Included hardware:
                                        </Text>
                                        <Stack gap={4}>
                                            {hardwareDetails[hardware]?.includedHardware?.map((item, i) => (
                                                <Group key={i} wrap="nowrap" gap="xs" align="flex-start">
                                                    <TbCheck size={16} style={{ marginTop: 4, flexShrink: 0 }} />
                                                    <Text fz="md" c="dimmed">
                                                        {item}
                                                    </Text>
                                                </Group>
                                            ))}
                                        </Stack>
                                    </Stack>
                                )}
                                {hardware === null && (
                                    <Text size="lg" c="dimmed">
                                        Select a hardware option or start with our support packages.
                                    </Text>
                                )}
                                {hardware && hardwareDetails[hardware] && hardwareDetails[hardware]?.images && (
                                    <Group className={cx.images}>
                                        {hardwareDetails[hardware]?.images?.map((item, index) => (
                                            <UnstyledButton
                                                key={index}
                                                className={cx.image}
                                                onClick={() =>
                                                    handleImageClick(hardwareDetails[hardware]?.images || [], index)
                                                }
                                            >
                                                <ImageBlock image={item.image} />
                                            </UnstyledButton>
                                        ))}
                                    </Group>
                                )}
                                <Group justify="flex-end">
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            handleSelectHardware(null);
                                            goNext();
                                        }}
                                        size="md"
                                    >
                                        Continue to Support
                                    </Button>
                                    <Button variant="filled" onClick={goNext} disabled={!hardware} size="md">
                                        Next
                                    </Button>
                                </Group>
                            </Stack>
                        </Stepper.Step>

                        {stepKeys.includes('license') && (
                            <Stepper.Step label="License" description="">
                                <Stack gap="md">
                                    <Group>
                                        <Text size="lg" fw={600}>
                                            Enter Managed Capacity
                                            <Tooltip
                                                position="top"
                                                withArrow
                                                w={300}
                                                multiline
                                                label="Managed Capacity is the sum of all rated power (in kW) of the power conversion units in your system, managed by the microgrid controller."
                                            >
                                                <ActionIcon
                                                    variant="transparent"
                                                    size="sm"
                                                    style={{ verticalAlign: 'middle' }}
                                                    ml={4}
                                                    mt={-1}
                                                >
                                                    <TbInfoCircle size={16} />
                                                </ActionIcon>
                                            </Tooltip>
                                        </Text>

                                        <Box
                                            className="animation-border-spin"
                                            style={{
                                                '--background-color': 'var(--mantine-color-green-1)',
                                                '--animation-color': 'var(--mantine-color-cyan-1)',
                                                '--border-width': '4px',
                                                borderRadius: 14,
                                            }}
                                        >
                                            <NumberInput
                                                size="xl"
                                                value={capacityMW}
                                                onChange={(v) => {
                                                    if (v === '' || v === undefined || v === null) {
                                                        setCapacityMW('');
                                                        return;
                                                    }
                                                    if (typeof v === 'string') {
                                                        const parsed = parseFloat(v);
                                                        setCapacityMW(Number.isFinite(parsed) ? parsed : 0);
                                                    } else {
                                                        setCapacityMW(v);
                                                    }
                                                }}
                                                min={0}
                                                step={0.1}
                                                clampBehavior="strict"
                                                allowDecimal={true}
                                                decimalScale={3}
                                                maw={150}
                                                rightSection={
                                                    <Text size="lg" c="dimmed" style={{ fontWeight: 600 }}>
                                                        MW
                                                    </Text>
                                                }
                                                rightSectionWidth={40}
                                                styles={{
                                                    root: {
                                                        width: '100%',
                                                    },
                                                    input: {
                                                        borderRadius: 10,
                                                        paddingLeft: 20,
                                                        paddingRight: 60,
                                                        fontWeight: 600,
                                                        fontSize: 16,
                                                        height: 44,
                                                        border: 'none',
                                                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)',
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Group>

                                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                                        <SelectCard
                                            title="Buy"
                                            description={`$${PRICES.licensePerKW.buyOneTime} /kW (one-time)`}
                                            descriptionSize="sm"
                                            price={IntlHelpers.formatPrice(estimateBuy, 0)}
                                            items={['One-time purchase for lifetime local control']}
                                            selected={licenseType === 'buy'}
                                            onClick={() => handleLicenseChange('buy')}
                                        />
                                    </SimpleGrid>
                                    {licenseType && licenseDetails[licenseType] && (
                                        <Stack gap={4}>
                                            {(licenseDetails[licenseType]?.bullets ?? []).map((b, i) => (
                                                <Text size="lg" c="dimmed" key={i}>
                                                    {b}
                                                </Text>
                                            ))}
                                            {licenseDetails[licenseType]?.note && (
                                                <Text size="lg" c="dimmed" fs="italic">
                                                    {licenseDetails[licenseType]?.note}
                                                </Text>
                                            )}
                                        </Stack>
                                    )}
                                    {licenseType === null && (
                                        <Text size="lg" c="dimmed">
                                            Select your software license to continue.
                                        </Text>
                                    )}
                                    <Group justify="space-between">
                                        <Button variant="default" onClick={goPrev} size="md">
                                            Back
                                        </Button>
                                        <Button variant="filled" onClick={goNext} disabled={!licenseType} size="md">
                                            Next
                                        </Button>
                                    </Group>
                                </Stack>
                            </Stepper.Step>
                        )}

                        {stepKeys.includes('support') && (
                            <Stepper.Step label="Support" description="">
                                <Stack gap="xl">
                                    <Stack gap="md">
                                        <Title order={4} fz="lg" fw={800}>
                                            Engineering Support via DCIDE Credits
                                        </Title>
                                        <Text size="lg">
                                            DEP engineering support for the Microgrid Controller is delivered through
                                            DCIDE credits. Incoming questions and messages are always free. When a
                                            request requires DEP engineering work, DEP provides a credit estimate before
                                            work begins. Work only starts after you approve the estimate.
                                        </Text>
                                        <Text size="lg" fw={600}>
                                            Credits can be used for:
                                        </Text>
                                        <Stack gap={4} pl="md">
                                            {[
                                                'Address-map generation & controller file configuration',
                                                'Commissioning support & device testing',
                                                'Custom application development',
                                                'Customer-specific troubleshooting',
                                                'Manufacturer coordination & third-party system integration',
                                            ].map((item, i) => (
                                                <Text size="md" c="dimmed" key={i}>
                                                    · {item}
                                                </Text>
                                            ))}
                                        </Stack>
                                        <Text size="md" c="dimmed" fs="italic">
                                            Credits are valid for one year from purchase. Your DCIDE workspace contains
                                            design files, controller context, support history, and operational data,
                                            helping move faster from question, to answer, to implementation.
                                        </Text>
                                    </Stack>

                                    <Card withBorder radius="md" p="lg">
                                        <Stack gap="md">
                                            <UnstyledButton onClick={() => setIncludeCredits((cur) => !cur)}>
                                                <Group justify="space-between">
                                                    <Text size="lg" fw={600}>
                                                        Add DCIDE Credits to this order
                                                    </Text>
                                                    <Checkbox
                                                        checked={includeCredits}
                                                        onChange={(e) => setIncludeCredits(e.currentTarget.checked)}
                                                        size="md"
                                                    />
                                                </Group>
                                            </UnstyledButton>
                                            {includeCredits && (
                                                <Stack gap={4}>
                                                    <Group align="flex-end">
                                                        <NumberInput
                                                            label="Number of credits"
                                                            value={creditsAmount}
                                                            onChange={(v) => {
                                                                if (v === '' || v === undefined || v === null) {
                                                                    setCreditsAmount('');
                                                                    return;
                                                                }
                                                                if (typeof v === 'string') {
                                                                    const parsed = parseFloat(v);
                                                                    setCreditsAmount(
                                                                        Number.isFinite(parsed) ? parsed : '',
                                                                    );
                                                                } else {
                                                                    setCreditsAmount(v);
                                                                }
                                                            }}
                                                            step={5000}
                                                            allowDecimal={false}
                                                            allowNegative={false}
                                                            thousandSeparator=","
                                                            size="md"
                                                            maw={240}
                                                            suffix=" credits"
                                                            error={creditsBelowMinimum || undefined}
                                                            styles={{
                                                                controls: { height: 34 },
                                                                control: { height: 17 },
                                                            }}
                                                        />
                                                        <Stack gap={0}>
                                                            <Text size="sm" c="dimmed">
                                                                Value
                                                            </Text>
                                                            <Text size="xl" fw={700}>
                                                                {IntlHelpers.formatPrice(creditsCost, 0)}
                                                            </Text>
                                                        </Stack>
                                                    </Group>
                                                    <Text size="sm" c={creditsBelowMinimum ? 'red' : 'dimmed'}>
                                                        {creditsBelowMinimum
                                                            ? `Minimum purchase is ${IntlHelpers.formatNumber(PRICES.credits.minimum)} credits.`
                                                            : `Minimum ${IntlHelpers.formatNumber(PRICES.credits.minimum)} credits.`}
                                                    </Text>
                                                </Stack>
                                            )}
                                        </Stack>
                                    </Card>

                                    <Group justify="space-between">
                                        <Button variant="default" onClick={goPrev} size="md">
                                            Back
                                        </Button>
                                        <Button
                                            variant="filled"
                                            onClick={goNext}
                                            disabled={creditsBelowMinimum}
                                            size="md"
                                        >
                                            Review
                                        </Button>
                                    </Group>
                                </Stack>
                            </Stepper.Step>
                        )}
                        {stepKeys.includes('summary') && (
                            <Stepper.Step label="Review" description="">
                                <Stack gap="xl">
                                    <Summary />
                                    {!showQuoteForm && (
                                        <Group justify="space-between">
                                            <Button variant="default" onClick={goPrev} size="md">
                                                Back
                                            </Button>
                                            <Button
                                                variant="filled"
                                                disabled={!hardware || creditsBelowMinimum}
                                                onClick={() => {
                                                    setQuoteSubmitted(false);
                                                    setShowQuoteForm(true);
                                                }}
                                                size="md"
                                            >
                                                Proceed to Checkout
                                            </Button>
                                        </Group>
                                    )}
                                    {showQuoteForm && (
                                        <Card withBorder p="lg" radius="md">
                                            <form onSubmit={quoteForm.onSubmit(handleSubmitQuote)}>
                                                <Stack gap="sm">
                                                    <Text size="lg" fw={800}>
                                                        Please fill out the form below to process your order
                                                    </Text>
                                                    <Group grow>
                                                        <TextInput
                                                            {...quoteForm.getInputProps('firstName')}
                                                            label="First Name"
                                                            required
                                                            size="md"
                                                        />
                                                        <TextInput
                                                            {...quoteForm.getInputProps('lastName')}
                                                            label="Last Name"
                                                            required
                                                            size="md"
                                                        />
                                                    </Group>
                                                    <TextInput
                                                        {...quoteForm.getInputProps('email')}
                                                        label="Email"
                                                        type="email"
                                                        required
                                                        size="md"
                                                    />
                                                    <TextInput
                                                        {...quoteForm.getInputProps('company')}
                                                        label="Company Name"
                                                        required
                                                        size="md"
                                                    />
                                                    <Group grow>
                                                        <TextInput
                                                            {...quoteForm.getInputProps('addressLine1')}
                                                            label="Address line 1"
                                                            required
                                                            size="md"
                                                        />
                                                        <TextInput
                                                            {...quoteForm.getInputProps('addressLine2')}
                                                            label="Address line 2"
                                                            size="md"
                                                        />
                                                    </Group>
                                                    <Group grow>
                                                        <TextInput
                                                            {...quoteForm.getInputProps('city')}
                                                            label="City"
                                                            required
                                                            size="md"
                                                        />
                                                        <TextInput
                                                            {...quoteForm.getInputProps('zip')}
                                                            label="ZIP Code"
                                                            required
                                                            size="md"
                                                        />
                                                    </Group>
                                                    <Select
                                                        {...quoteForm.getInputProps('country')}
                                                        label="Country"
                                                        data={COUNTRIES}
                                                        searchable
                                                        nothingFoundMessage="No country found"
                                                        required
                                                        size="md"
                                                    />
                                                    <Textarea
                                                        {...quoteForm.getInputProps('message')}
                                                        label="Message"
                                                        placeholder="Add any details or questions"
                                                        autosize
                                                        minRows={3}
                                                        size="md"
                                                    />
                                                    <Group justify="space-between" mt="sm">
                                                        <Button
                                                            variant="default"
                                                            type="button"
                                                            onClick={() => setShowQuoteForm(false)}
                                                            size="md"
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button type="submit" variant="filled" size="md">
                                                            Buy Now
                                                        </Button>
                                                    </Group>
                                                    <Group justify="right">
                                                        <Text size="md">
                                                            Our team will confirm your order and payment details before
                                                            processing.
                                                        </Text>
                                                    </Group>
                                                </Stack>
                                            </form>
                                        </Card>
                                    )}
                                    {quoteSubmitted && !showQuoteForm && (
                                        <Alert color="green" p="lg" radius="md">
                                            <Text size="lg">
                                                Thank you for your quote request! Our team will contact you shortly.
                                            </Text>
                                        </Alert>
                                    )}
                                </Stack>
                            </Stepper.Step>
                        )}
                    </Stepper>
                </Stack>
            </Card>
        </Box>
    );
};

export default PriceConfigurator;
